import {
  Avatar,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdAccessTime, MdBookmarkBorder, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import ExamQuestions from "../../../containers/student/exams/exam-questions";
import ExamAnswersLoading from "../../../containers/student/models/exam-answers-loading";
import {
  closeExamAnswersLoading,
  openExamAnswersLoading,
} from "../../../modules/admin-modules/models/Actions";
import {
  getExamQuestionsRequest,
  getExamsByIdRequest,
  sendAnswersRequest,
} from "../../../modules/students-modules/exams/Actions";
import { getStudentExamQuestionsRequest } from "../../../modules/students-modules/student-exams/Actions";

const FONT_STACK = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;
const SERIF_Q = `'Amiri','Scheherazade New','Noto Sans Arabic',serif`;

const C = {
  primary: "#6A1E1E",
  accent: "#4A0F1F",
  pageBg: "#E9E2DB",
  border: "#D8CEC3",
  cream: "#FAF2EB",
  ink: "#1e1b17",
};

const POST_EXAM_REDIRECT_KEY = "edutopia_post_exam_redirect";

function formatHMS(targetMs) {
  const ms = Math.max(0, targetMs - Date.now());
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function ExamHeaderCountdown({ exam, onExpire }) {
  const [display, setDisplay] = useState("00:00:00");
  const firedRef = useRef(false);

  const targetMs = useMemo(() => {
    if (exam?.timer != 1) return null;
    const mins = Number(exam?.time);
    if (!Number.isFinite(mins) || mins <= 0) return null;
    const raw = typeof window !== "undefined" ? window.localStorage.getItem("startTime") : null;
    let startMs = raw ? new Date(raw).getTime() : Date.now();
    if (!Number.isFinite(startMs)) startMs = Date.now();
    return startMs + mins * 60 * 1000;
  }, [exam?.timer, exam?.time, exam?.id]);

  useEffect(() => {
    if (targetMs == null || !Number.isFinite(targetMs)) return undefined;
    firedRef.current = false;
    const tick = () => {
      setDisplay(formatHMS(targetMs));
      if (Date.now() >= targetMs && !firedRef.current) {
        firedRef.current = true;
        onExpire?.();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetMs, onExpire]);

  if (targetMs == null) return null;

  return (
    <HStack
      spacing={2}
      px={4}
      py={2}
      borderWidth="1px"
      borderColor={C.border}
      bg={C.cream}
      borderRadius="xl"
    >
      <MdAccessTime color={C.primary} size={22} />
      <Text fontFamily="mono" fontWeight="800" fontSize="xl" color={C.primary}>
        {display}
      </Text>
    </HStack>
  );
}

const SolveExam = () => {
  const [examQuestions, setExamQuestions] = useState([]);
  const dispatch = useDispatch();
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const examId = window.location.pathname.includes("student")
    ? window.location.pathname.split("/")[3]
    : window.location.pathname.split("/")[2];

  const examQuestionsData = useSelector((state) => state.chapterExams.examQuestions);
  const studentExamQuestions = useSelector((state) => state.studentExams.examQuestions);
  const userData = useSelector((state) => state.user.userData);

  const [answers, setAnswers] = useState([]);
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(0);
  const exam = useSelector((state) => state.chapterExams.exam);
  const isLoading = useSelector((state) => state.chapterExams.isLoading);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (examId) {
      dispatch(getExamsByIdRequest(examId));
      if (window.location.pathname.includes("student")) {
        dispatch(getStudentExamQuestionsRequest(examId));
      } else {
        dispatch(getExamQuestionsRequest(examId));
      }
    }
  }, [examId, dispatch]);

  useEffect(() => {
    if (window.location.pathname.includes("student")) {
      setExamQuestions(studentExamQuestions);
    } else {
      setExamQuestions(examQuestionsData);
    }
  }, [studentExamQuestions, examQuestionsData]);

  const handelNextQuestion = () => {
    if (selectedQuestionIdx < 0) setSelectedQuestionIdx(examQuestions?.length - 1);
    else if (selectedQuestionIdx >= examQuestions?.length - 1) setSelectedQuestionIdx(0);
    else setSelectedQuestionIdx((s) => s + 1);
  };

  const handelPrevQuestion = () => {
    if (selectedQuestionIdx > examQuestions?.length - 1) setSelectedQuestionIdx(0);
    if (selectedQuestionIdx <= 0) setSelectedQuestionIdx(examQuestions?.length - 1);
    else setSelectedQuestionIdx((s) => s - 1);
  };

  const answeredCount = useMemo(() => {
    if (!examQuestions?.length) return 0;
    let n = 0;
    for (let i = 0; i < examQuestions.length; i += 1) {
      if (answers[i]?.answer_id != null) n += 1;
    }
    return n;
  }, [answers, examQuestions]);

  const allAnswered = examQuestions?.length > 0 && answeredCount === examQuestions.length;

  const handelSendAnswersRef = useRef(() => {});
  const handelSendAnswers = useCallback(async () => {
    dispatch(openExamAnswersLoading());
    const action = () => {
      dispatch(closeExamAnswersLoading());
      window.localStorage.removeItem("startTime");
      const saved = sessionStorage.getItem(POST_EXAM_REDIRECT_KEY);
      sessionStorage.removeItem(POST_EXAM_REDIRECT_KEY);
      if (saved) {
        window.location.href = saved;
        return;
      }
      window.location.pathname.includes("student")
        ? (window.location.href = "/student/exams")
        : (window.location.href = "/exams/sections");
    };

    const formData = new FormData();
    formData.append("exam_id", examId);
    const n = examQuestions?.length || 0;
    for (let index = 0; index < n; index += 1) {
      const data = answers[index];
      if (data?.question_id != null) {
        formData.append(`answers[${index}][question_id]`, data.question_id);
      }
      if (data?.answer_id != null) {
        formData.append(`answers[${index}][answer_id]`, data.answer_id);
      }
    }

    dispatch(sendAnswersRequest(formData, action));
  }, [answers, dispatch, examId, examQuestions]);

  handelSendAnswersRef.current = handelSendAnswers;

  const handleTimerExpire = useCallback(() => {
    handelSendAnswersRef.current?.();
  }, []);

  const currentQ = examQuestions[selectedQuestionIdx];
  const sectionTitle =
    currentQ?.section_name ||
    currentQ?.section?.name ||
    currentQ?.part_name ||
    (parsed?.name ? String(parsed.name) : "Exam");

  const gridPattern = {
    bg: C.pageBg,
    backgroundImage: "radial-gradient(#D8CEC3 1px, transparent 1px)",
    backgroundSize: "32px 32px",
  };

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" {...gridPattern} fontFamily={FONT_STACK}>
        <Spinner size="xl" thickness="4px" color={C.primary} />
      </Flex>
    );
  }

  return (
    <>
      <Box
        minH="100vh"
        fontFamily={FONT_STACK}
        color={C.ink}
        {...gridPattern}
        pt={{ base: "88px", md: "96px" }}
        pb="120px"
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
      >
        {/* Fixed header */}
        <Flex
          as="header"
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={50}
          px={{ base: 4, md: 8 }}
          py={4}
          bg={C.pageBg}
          borderBottomWidth="1px"
          borderColor={C.border}
          align="center"
          justify="space-between"
          gap={4}
          flexWrap="wrap"
        >
          <Heading
            as="h2"
            fontSize={{ base: "sm", md: "lg" }}
            fontWeight="800"
            color={C.primary}
            textTransform="uppercase"
            letterSpacing="0.06em"
            noOfLines={2}
            maxW={{ base: "200px", md: "50%" }}
          >
            {parsed?.name || exam?.name || "Exam"}
          </Heading>
          <HStack spacing={{ base: 3, md: 6 }} flexShrink={0}>
            <ExamHeaderCountdown exam={exam} onExpire={handleTimerExpire} />
            <Button
              bg={C.accent}
              color="white"
              size="sm"
              px={{ base: 4, md: 8 }}
              py={6}
              borderRadius="xl"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.14em"
              fontSize="xs"
              isDisabled={!allAnswered}
              _hover={{ bg: C.primary }}
              onClick={onOpen}
            >
              Submit exam
            </Button>
            <Avatar
              size="sm"
              name={userData?.name || userData?.username || "Student"}
              src={userData?.image || userData?.avatar}
              borderRadius="md"
              borderWidth="1px"
              borderColor={C.border}
            />
          </HStack>
        </Flex>

        <Box maxW="5xl" mx="auto" px={{ base: 4, md: 8 }}>
          <Flex
            align={{ base: "flex-start", md: "flex-end" }}
            justify="space-between"
            gap={4}
            mb={8}
            direction={{ base: "column", md: "row" }}
          >
            <Box>
              <Text
                fontSize="xs"
                fontWeight="800"
                color={`${C.primary}B3`}
                textTransform="uppercase"
                letterSpacing="0.2em"
              >
                Question {selectedQuestionIdx + 1} of {examQuestions?.length || 0}
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="800"
                color={C.primary}
                mt={2}
                fontFamily={FONT_STACK}
                lineHeight="1.15"
              >
                {sectionTitle}
              </Heading>
            </Box>
            <HStack spacing={1}>
              <IconButton
                aria-label="Previous question"
                icon={<MdChevronLeft size={28} />}
                variant="outline"
                borderColor={C.border}
                bg="white"
                borderRadius="lg"
                onClick={handelPrevQuestion}
              />
              <IconButton
                aria-label="Next question"
                icon={<MdChevronRight size={28} />}
                variant="outline"
                borderColor={C.border}
                bg="white"
                borderRadius="lg"
                onClick={handelNextQuestion}
              />
            </HStack>
          </Flex>

          <Box position="relative" mb={8}>
            <ExamQuestions
              data={currentQ}
              setAnswers={setAnswers}
              answers={answers}
              selectedQuestionIdx={selectedQuestionIdx}
              design="academic"
              questionSerifFont={SERIF_Q}
            />
          </Box>

          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Button
              variant="ghost"
              color={C.primary}
              fontWeight="800"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="0.14em"
              leftIcon={<MdBookmarkBorder size={18} />}
              isDisabled
              opacity={0.45}
              _hover={{}}
            >
              Mark for review
            </Button>
            <Button
              bg={C.primary}
              color="white"
              px={{ base: 8, md: 14 }}
              py={6}
              borderRadius="xl"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.12em"
              fontSize="sm"
              _hover={{ bg: C.accent }}
              boxShadow="md"
              onClick={handelNextQuestion}
            >
              Next question
            </Button>
          </Flex>
        </Box>

        {/* Fixed footer — question grid */}
        <Flex
          as="footer"
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex={40}
          bg="white"
          borderTopWidth="1px"
          borderColor={C.border}
          px={{ base: 3, md: 8 }}
          py={3}
          align="center"
          justify="space-between"
          flexWrap="wrap"
          gap={3}
          boxShadow="0 -4px 10px rgba(0,0,0,0.05)"
        >
          <HStack
            spacing={1.5}
            overflowX="auto"
            pb={1}
            css={{
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
            flex="1"
            minW={0}
          >
            {examQuestions?.map((_, index) => {
              const answered = answers[index]?.answer_id != null;
              const isCurrent = selectedQuestionIdx === index;
              return (
                <Button
                  key={index}
                  flexShrink={0}
                  minW="36px"
                  h="36px"
                  p={0}
                  fontSize="11px"
                  fontWeight="800"
                  borderRadius="md"
                  variant="unstyled"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg={answered ? C.primary : "white"}
                  color={answered ? "white" : C.primary}
                  borderWidth={isCurrent ? "2px" : "1px"}
                  borderColor={isCurrent ? C.primary : C.border}
                  boxShadow={isCurrent ? "0 0 0 1px rgba(106,30,30,0.15)" : "none"}
                  onClick={() => setSelectedQuestionIdx(index)}
                >
                  {String(index + 1).padStart(2, "0")}
                </Button>
              );
            })}
          </HStack>
          <HStack
            spacing={{ base: 4, md: 6 }}
            pl={{ base: 0, md: 8 }}
            borderLeftWidth={{ base: 0, md: "1px" }}
            borderColor={C.border}
            flexShrink={0}
            display={{ base: "none", lg: "flex" }}
          >
            <HStack spacing={2}>
              <Box w="12px" h="12px" bg={C.primary} borderRadius="sm" />
              <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase">
                Answered
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Box w="12px" h="12px" bg="white" borderWidth="2px" borderColor={C.primary} borderRadius="sm" />
              <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase">
                Current
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Box w="12px" h="12px" bg="white" borderWidth="1px" borderColor={C.border} borderRadius="sm" />
              <Text fontSize="11px" fontWeight="800" color="gray.600" textTransform="uppercase">
                Unvisited
              </Text>
            </HStack>
          </HStack>
        </Flex>
      </Box>

      <ExamAnswersLoading />

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl" fontFamily={FONT_STACK}>
          <ModalHeader color={C.primary}>Confirm submission</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="md" color={C.ink}>
              Are you sure you want to submit your answers? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} color={C.accent} fontWeight="700">
              Cancel
            </Button>
            <Button
              bg={C.primary}
              color="white"
              borderRadius="lg"
              onClick={() => {
                onClose();
                handelSendAnswers();
              }}
              _hover={{ bg: C.accent }}
            >
              Yes, submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SolveExam;
